import React, { useMemo, useState } from "react";

import { HeaderSpacer } from "../../_layout-helpers/spacers";
import { Spacer } from "../../../components/_shared-components/arrangement/Space";
import { ContentContainer } from "../../../components/_shared-components/arrangement/Container";
import { StoriesOverviewComponent } from "../../../components/StoriesOverviewComponent/Stories/stories-overview-component";
import { HeaderComponent } from "../../../components/HeaderComponent/HeaderComponent";
import { FooterComponent } from "../../../components/FooterComponent/FooterComponent";
import { UITabs } from "../../../components/StoriesOverviewComponent/ui-tabs/UITabs";
import { useLocation } from "react-router";
import { PageMetadataComponent } from "../../../components/PageMetadataComponent/PageMetadataComponent";
import {
  ExtractSwarovskiPageData,
  StoriesOverviewSectionType,
  StoriesOverviewSectionTypeT,
  StoryTeaser,
} from "@swo/storefront";
import { StoriesOverviewPageData } from "@swo/storefront";
import { useStore } from "../../../store/hooks/use-store";
import { pipe } from "fp-ts/lib/function";
import { array, option, set, string } from "fp-ts";
import { ExpandedTagT, nonEmpty, TagExpanderF, TagT, urlWithTagClicked } from "@swo/shared";
import { Eq } from "fp-ts/es6/Eq";
import { TAGS_QS_NAME } from "@swo/shared";
import { T } from "../../../components/T";

export const StoriesOverviewPageLayout: React.FC<ExtractSwarovskiPageData<StoriesOverviewPageData>> = props => {
  const { search: rawSearch, pathname } = useLocation();
  const { locationOrigin } = useStore().state;

  const currentTags = pipe(
    decodedSearch(rawSearch)
      .getAll(TAGS_QS_NAME)
      .flatMap(tag => tag.split(",").map(t => t.toLowerCase()))
      .filter(nonEmpty),
    array.uniq(string.Eq),
  );

  const tabElements = [
    { label: <T>{props.data.storiesLabel}</T>, link: pathname, isActive: true },
    { label: <T>{props.data.videosLabel}</T>, link: props.data.videoStoriesOverviewLink, isActive: false },
  ];

  const activeTagFilters = useMemo(() => {
    const allPossibleTags = getAllPossibleTags(props.data.sections);

    return getActiveTagFilters(allPossibleTags, currentTags);
  }, [rawSearch, props.data.sections]);

  const expandedActiveTagFilters = activeTagFilters.map(expandTagSlug(true));

  function expandTagSlug(deleteAlreadyActiveTags: boolean): (t: TagT) => ExpandedTagT {
    const searchParams = new URLSearchParams();
    currentTags.forEach(tag => {
      searchParams.append(TAGS_QS_NAME, tag);
    });

    return t => ({
      ...t,
      expandedLink: urlWithTagClicked(pathname, searchParams.toString(), t.link, deleteAlreadyActiveTags),
    });
  }

  const expandedSections = expandSectionsTags(props.data.sections, expandTagSlug(false));
  const filteredElements =
    activeTagFilters.length > 0 ? getFilteredElements(expandedSections, activeTagFilters) : expandedSections;

  return (
    <React.Fragment>
      <PageMetadataComponent
        {...props.data.metadata}
        locationOrigin={locationOrigin}
        canonicalPath={props.page.path}
        languageIsoCode={props.locale.language.isoCode}
      />
      <HeaderComponent header={props.data.header} version={"normal"} />
      <HeaderSpacer />

      <ContentContainer>
        {props.page.type === "educational" ? null : (
          <>
            <UITabs elements={tabElements} />
            <Spacer size={"md"} />
          </>
        )}

        <StoriesOverviewComponent
          elements={filteredElements}
          activeTags={expandedActiveTagFilters}
          noMatchingStoriesLabel={props.data.noMatchingStoriesLabel}
        />
      </ContentContainer>

      <Spacer size={"lg"} />
      <FooterComponent {...props.data.footer} />
    </React.Fragment>
  );
};

export type ExpandedStoryTeaser = Omit<StoryTeaser, "tags"> & { tags: Array<ExpandedTagT> };

function expandSectionsTags(
  sections: Array<StoriesOverviewSectionType>,
  tagExpander: TagExpanderF,
): Array<StoriesOverviewSectionTypeT<ExpandedStoryTeaser>> {
  return sections.map(section => {
    const mainTeaser = section.mainTeaser
      ? { ...section.mainTeaser, tags: section.mainTeaser.tags.map(tagExpander) }
      : undefined;

    const stickyTeaser = {
      ...section.stickyTeaser,
      tags: section.stickyTeaser.tags.map(tagExpander),
    };

    const teasers = section.teasers.map(teaser => {
      return { ...teaser, tags: teaser.tags.map(tagExpander) };
    });

    return { ...section, mainTeaser, stickyTeaser, teasers };
  });
}

function getFilteredElements(
  sections: Array<StoriesOverviewSectionTypeT<ExpandedStoryTeaser>>,
  activeTagFilters: Array<TagT>,
): Array<StoriesOverviewSectionTypeT<ExpandedStoryTeaser>> {
  const flattenedSections = sections.flatMap(section => {
    return [...(section.mainTeaser ? [section.mainTeaser] : []), section.stickyTeaser, ...section.teasers];
  });

  const filteredFlattenedSections = flattenedSections.filter(s => {
    return activeTagFilters.every(activeFilterTag =>
      s.tags.map(t => t.link.toLowerCase()).includes(activeFilterTag.link.toLowerCase()),
    );
  });

  const [first, ...rest] = filteredFlattenedSections;

  return pipe(
    option.fromNullable(first),
    option.map(first => [
      {
        layout: "1 big and 3 small teasers" as const,
        mainTeaser: undefined,
        stickyTeaser: first,
        teasers: rest,
      },
    ]),
    option.getOrElseW(() => []),
  );
}

function getAllPossibleTags(sections: Array<StoriesOverviewSectionType>): Set<TagT> {
  const tags = sections.flatMap(section => {
    const x = section.mainTeaser?.tags || [];
    const y = section.stickyTeaser.tags;
    const z = section.teasers.flatMap(t => t.tags);

    return [...x, ...y, ...z];
  });

  return pipe(tags, set.fromArray(linkEq));
}

const linkEq: Eq<{ label: string; link: string }> = { equals: (a, b) => a.link.toLowerCase() === b.link.toLowerCase() };

function decodedSearch(routerLocationSearch: string) {
  // For some reason I currently don't understand,
  // 'search' on the server-side is encoded as
  // "?tags=innovation%2Csustainability%2Cabout-us",
  // As a work-around, let's patch it

  return pipe(routerLocationSearch, decodeURIComponent, (str: string) => new URLSearchParams(str));
}

export function getActiveTagFilters(allPossibleTags: Set<TagT>, currentTags: Array<string>): Array<TagT> {
  const possibleTags = Array.from(allPossibleTags.values());

  return currentTags.flatMap(urlTagLink => {
    return possibleTags.find(t => t.link.toLowerCase() === urlTagLink) || [];
  });
}
